import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import { RefreshCw, Search, ChevronDown, Plus, Edit2, Trash2, XCircle, Upload, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { showDeleteConfirmation } from '../lib/sweetAlert';

interface SponsorshipPlan {
  name: string;
  price: number;
  description?: string;
}

interface Sponsorship {
  _id: string;
  poojaName: string;
  poojaType: string;
  deity: string;
  description?: string;
  image?: string;
  sponsorshipType: 'one-time' | 'limited' | 'unlimited';
  maxSponsors?: number;
  schedule: {
    frequency: 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    date?: string;
    time: string;
    daysOfWeek?: string[];
    dayOfMonth?: number;
    monthOfYear?: number;
    description?: string;
  };
  plans: SponsorshipPlan[];
  status: 'draft' | 'published' | 'inactive';
  templeId: string | { _id: string; name: string };
  createdAt?: string;
}

const SponsorshipsPage: React.FC = () => {
  const { user } = useAuth();
  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([]);
  const [temples, setTemples] = useState<{ _id: string; name: string; deities?: any[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [templeFilter, setTempleFilter] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSponsorship, setEditingSponsorship] = useState<Sponsorship | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const [formData, setFormData] = useState({
    poojaName: '',
    poojaType: '',
    deity: '',
    description: '',
    sponsorshipType: 'unlimited' as 'one-time' | 'limited' | 'unlimited',
    maxSponsors: 1,
    status: 'published' as 'draft' | 'published' | 'inactive',
    templeId: '',
    schedule: {
      frequency: 'daily' as any,
      time: '08:00',
      daysOfWeek: [] as string[],
      dayOfMonth: 1,
      monthOfYear: 0,
      description: ''
    },
    plans: [{ name: 'Basic Sponsorship', price: 0, description: '' }] as SponsorshipPlan[]
  });

  const fetchTemples = useCallback(async () => {
    try {
      const res = await adminApiClient.getTemples();
      if (res.success && res.data?.temples) setTemples(res.data.temples);
    } catch (err) {
      console.error('Failed to fetch temples', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApiClient.getSponsorships({
        limit: 200,
        ...(search && { search }),
        ...(isSuperAdmin && templeFilter && templeFilter !== 'all' && { templeId: templeFilter }),
      });
      if (res.success && (res as any).data?.sponsorships) {
        setSponsorships((res as any).data.sponsorships);
      }
    } catch {
      toast.error('Failed to fetch sponsorships');
    } finally {
      setLoading(false);
    }
  }, [search, isSuperAdmin, templeFilter]);

  useEffect(() => {
    fetchTemples();
  }, [fetchTemples]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (s: any) => {
    setEditingSponsorship(s);
    setFormData({
      poojaName: s.poojaName || s.title || '',
      poojaType: s.poojaType || '',
      deity: s.deity || '',
      description: s.description || '',
      sponsorshipType: s.sponsorshipType || 'unlimited',
      maxSponsors: s.maxSponsors || 1,
      status: s.status || 'published',
      templeId: typeof s.templeId === 'object' ? s.templeId?._id : s.templeId || '',
      schedule: {
        frequency: s.schedule?.frequency || 'daily',
        time: s.schedule?.time || '08:00',
        daysOfWeek: s.schedule?.daysOfWeek || [],
        dayOfMonth: s.schedule?.dayOfMonth || 1,
        monthOfYear: s.schedule?.monthOfYear || 0,
        description: s.schedule?.description || ''
      },
      plans: Array.isArray(s.plans) && s.plans.length > 0 
        ? s.plans 
        : (s.amount ? [{ name: 'Sponsorship', price: s.amount, description: '' }] : [{ name: 'Basic Sponsorship', price: 0, description: '' }])
    });
    setImagePreview(s.image || null);
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showDeleteConfirmation('Delete Sponsorship?', 'This will permanently remove this sponsorship offering.');
    if (!confirmed) return;

    try {
      const res = await adminApiClient.deleteSponsorship(id);
      if (res.success) {
        toast.success('Sponsorship deleted');
        fetchData();
      } else {
        toast.error(res.message || 'Delete failed');
      }
    } catch {
      toast.error('Error deleting sponsorship');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingSponsorship ? 'Updating sponsorship...' : 'Creating sponsorship...');

    try {
      const data = new FormData();
      data.append('poojaName', formData.poojaName);
      data.append('poojaType', formData.poojaType);
      data.append('deity', formData.deity);
      data.append('description', formData.description);
      data.append('sponsorshipType', formData.sponsorshipType);
      data.append('maxSponsors', formData.maxSponsors.toString());
      data.append('status', formData.status);
      
      if (!isSuperAdmin) {
        // For temple admin, templeId is handled by backend or from user context, 
        // but we can send it if we have it.
        const tId = typeof formData.templeId === 'string' ? formData.templeId : (user as any)?.templeId;
        if (tId) data.append('templeId', tId);
      } else {
        data.append('templeId', formData.templeId);
      }

      data.append('schedule', JSON.stringify(formData.schedule));
      data.append('plans', JSON.stringify(formData.plans));

      if (selectedImage) {
        data.append('image', selectedImage);
      }

      let res;
      if (editingSponsorship) {
        res = await adminApiClient.updateSponsorship(editingSponsorship._id, data);
      } else {
        res = await adminApiClient.createSponsorship(data);
      }

      if (res.success) {
        toast.success(editingSponsorship ? 'Sponsorship updated' : 'Sponsorship created', { id: loadingToast });
        setShowForm(false);
        fetchData();
      } else {
        toast.error(res.message || 'Operation failed', { id: loadingToast });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving sponsorship', { id: loadingToast });
    }
  };

  const addPlan = () => {
    setFormData({
      ...formData,
      plans: [...formData.plans, { name: '', price: 0, description: '' }]
    });
  };

  const removePlan = (index: number) => {
    if (formData.plans.length <= 1) return;
    setFormData({
      ...formData,
      plans: formData.plans.filter((_, i) => i !== index)
    });
  };

  const updatePlan = (index: number, field: keyof SponsorshipPlan, value: any) => {
    const newPlans = [...formData.plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setFormData({ ...formData, plans: newPlans });
  };

  const columns: ColumnDef<Sponsorship>[] = [
    {
      accessorKey: 'poojaName',
      header: 'Pooja Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.image ? (
            <img src={row.original.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
              <Gift size={20} />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{row.original.poojaName || (row.original as any).title || 'Untitled'}</div>
            <div className="text-xs text-gray-500">{row.original.poojaType || 'General'} • {row.original.deity || 'Multiple Deities'}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'templeId',
      header: 'Temple',
      cell: ({ row }) => {
        const temple = row.original.templeId;
        return temple && typeof temple === 'object' ? (temple as any).name : '-';
      },
    },
    {
      accessorKey: 'sponsorshipType',
      header: 'Type',
      cell: ({ row }) => (
        <span className="capitalize text-sm text-gray-600">{row.original.sponsorshipType}</span>
      ),
    },
    {
      accessorKey: 'plans',
      header: 'Starting Price',
      cell: ({ row }) => {
        const plans = row.original.plans || [];
        const prices = plans.map(p => p.price);
        const minPrice = prices.length > 0 ? Math.min(...prices) : (row.original as any).amount || 0;
        return <span className="font-medium">${minPrice.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={`capitalize px-2 py-0.5 rounded text-xs font-medium ${
            row.original.status === 'published'
              ? 'bg-green-100 text-green-700'
              : row.original.status === 'draft'
              ? 'bg-gray-100 text-gray-600'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <button onClick={() => handleEdit(row.original)} className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
            <Edit2 size={16} />
          </button>
          <button onClick={() => handleDelete(row.original._id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            <Trash2 size={16} />
          </button>
        </div>
      ),
    }
  ];

  const filteredSponsorships = sponsorships.filter(
    (s) =>
      !search ||
      (s.poojaName || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.deity || '').toLowerCase().includes(search.toLowerCase())
  );

  const resetForm = () => {
    setEditingSponsorship(null);
    setFormData({
      poojaName: '',
      poojaType: '',
      deity: '',
      description: '',
      sponsorshipType: 'unlimited',
      maxSponsors: 1,
      status: 'published',
      templeId: isSuperAdmin ? '' : (user as any)?.templeId || '',
      schedule: {
        frequency: 'daily',
        time: '08:00',
        daysOfWeek: [],
        dayOfMonth: 1,
        monthOfYear: 0,
        description: ''
      },
      plans: [{ name: 'Basic Sponsorship', price: 0, description: '' }]
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sponsorships</h1>
          <p className="text-sm text-gray-500">Manage sponsorship offerings and poojas</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition shadow-sm"
          >
            <Plus size={18} /> Add Sponsorship
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search sponsorships..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
          />
        </div>
        {isSuperAdmin && temples.length > 0 && (
          <div className="relative">
            <select
              value={templeFilter}
              onChange={(e) => setTempleFilter(e.target.value)}
              className="pl-4 pr-10 py-2 border border-gray-200 rounded-lg text-sm appearance-none bg-white min-w-[180px] focus:ring-2 focus:ring-orange-500/20 outline-none"
            >
              <option value="all">All temples</option>
              {temples.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredSponsorships}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl animate-in fade-in zoom-in duration-200 my-8">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">{editingSponsorship ? 'Edit Sponsorship' : 'New Sponsorship Offering'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:bg-gray-100 p-2 rounded-full transition-colors">
                <XCircle size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                  
                  {isSuperAdmin && !editingSponsorship && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Temple *</label>
                      <select
                        required
                        value={formData.templeId}
                        onChange={e => setFormData({ ...formData, templeId: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                      >
                        <option value="">Choose a temple</option>
                        {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}

                  {editingSponsorship && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temple</label>
                      <input
                        type="text"
                        disabled
                        value={typeof editingSponsorship.templeId === 'object' ? editingSponsorship.templeId.name : temples.find(t => t._id === editingSponsorship.templeId)?.name || 'Unknown Temple'}
                        className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500 outline-none cursor-not-allowed"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pooja Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.poojaName}
                      onChange={e => setFormData({ ...formData, poojaName: e.target.value })}
                      placeholder="e.g. Monthly Ganesh Homam"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pooja Type *</label>
                      <input
                        type="text"
                        required
                        value={formData.poojaType}
                        onChange={e => setFormData({ ...formData, poojaType: e.target.value })}
                        placeholder="e.g. Archana, Homam"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deity *</label>
                      <input
                        type="text"
                        required
                        value={formData.deity}
                        onChange={e => setFormData({ ...formData, deity: e.target.value })}
                        placeholder="e.g. Lord Ganesha"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sponsorship Type</label>
                      <select
                        value={formData.sponsorshipType}
                        onChange={e => setFormData({ ...formData, sponsorshipType: e.target.value as any })}
                        className="w-full px-3 py-2 border rounded-lg outline-none"
                      >
                        <option value="unlimited">Unlimited</option>
                        <option value="limited">Limited</option>
                        <option value="one-time">One-time Only</option>
                      </select>
                    </div>
                    {formData.sponsorshipType === 'limited' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Sponsors</label>
                        <input
                          type="number"
                          min="1"
                          value={formData.maxSponsors}
                          onChange={e => setFormData({ ...formData, maxSponsors: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border rounded-lg outline-none"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="flex gap-4">
                      {['published', 'draft', 'inactive'].map(s => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="status"
                            checked={formData.status === s}
                            onChange={() => setFormData({ ...formData, status: s as any })}
                            className="text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-sm capitalize text-gray-600">{s}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Media & Schedule */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 border-b pb-2">Media & Schedule</h3>
                  
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
                    <div className="flex items-start gap-4">
                      <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 shrink-0">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <Upload size={24} className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id="image-upload"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setSelectedImage(file);
                              setImagePreview(URL.createObjectURL(file));
                            }
                          }}
                        />
                        <label
                          htmlFor="image-upload"
                          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer shadow-sm transition-colors"
                        >
                          {imagePreview ? 'Change Image' : 'Select Image'}
                        </label>
                        <p className="text-xs text-gray-500">Recommended size: 800x600px. Max 5MB.</p>
                        {imagePreview && (
                          <button
                            type="button"
                            onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                            className="block text-xs text-red-600 hover:underline"
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-xl space-y-4">
                    <h4 className="text-sm font-semibold text-gray-700">Schedule Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Frequency</label>
                        <select
                          value={formData.schedule.frequency}
                          onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, frequency: e.target.value as any } })}
                          className="w-full px-2 py-1.5 border rounded text-sm outline-none"
                        >
                          <option value="once">Once</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                        <input
                          type="time"
                          value={formData.schedule.time}
                          onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, time: e.target.value } })}
                          className="w-full px-2 py-1.5 border rounded text-sm outline-none"
                        />
                      </div>
                    </div>

                    {formData.schedule.frequency === 'weekly' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-2">Days of Week</label>
                        <div className="flex flex-wrap gap-2">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const current = formData.schedule.daysOfWeek || [];
                                const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
                                setFormData({ ...formData, schedule: { ...formData.schedule, daysOfWeek: next } });
                              }}
                              className={`px-2 py-1 rounded text-xs transition-colors ${
                                formData.schedule.daysOfWeek?.includes(day)
                                  ? 'bg-orange-600 text-white'
                                  : 'bg-white border text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {day.slice(0, 3)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {(formData.schedule.frequency === 'monthly' || formData.schedule.frequency === 'yearly') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Day of Month</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={formData.schedule.dayOfMonth}
                          onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, dayOfMonth: parseInt(e.target.value) } })}
                          className="w-full px-2 py-1.5 border rounded text-sm outline-none"
                        />
                      </div>
                    )}

                    {formData.schedule.frequency === 'yearly' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
                        <select
                          value={formData.schedule.monthOfYear}
                          onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, monthOfYear: parseInt(e.target.value) } })}
                          className="w-full px-2 py-1.5 border rounded text-sm outline-none"
                        >
                          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                            <option key={m} value={i}>{m}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Plans */}
              <div className="mt-8 space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <h3 className="font-semibold text-gray-900">Sponsorship Plans</h3>
                  <button
                    type="button"
                    onClick={addPlan}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Plan
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.plans.map((plan, idx) => (
                    <div key={idx} className="flex gap-4 items-start bg-gray-50 p-4 rounded-xl animate-in slide-in-from-top-2 duration-200">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Plan Name *</label>
                          <input
                            type="text"
                            required
                            value={plan.name}
                            onChange={e => updatePlan(idx, 'name', e.target.value)}
                            placeholder="e.g. Bronze Sponsor"
                            className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Price *</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                            <input
                              type="number"
                              required
                              min="0"
                              value={plan.price}
                              onChange={e => updatePlan(idx, 'price', parseFloat(e.target.value))}
                              className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Short Description</label>
                          <input
                            type="text"
                            value={plan.description}
                            onChange={e => updatePlan(idx, 'description', e.target.value)}
                            placeholder="What's included?"
                            className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500/20"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePlan(idx)}
                        disabled={formData.plans.length === 1}
                        className="mt-7 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-0"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-8 mt-8 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 shadow-lg shadow-orange-500/30 transition-all active:scale-95"
                >
                  {editingSponsorship ? 'Save Changes' : 'Create Sponsorship'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorshipsPage;

